import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { StaffUser } from '../types';
import { socketManager } from '../lib/websocket';

interface AuthState {
  user: StaffUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  storeId: string | null;
  storeName: string | null;
  currency: string;
  mustChangePassword: boolean;

  setAuth: (user: StaffUser, token: string, refreshToken: string, storeId: string, storeName: string, currency: string, mustChangePassword?: boolean) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<StaffUser>) => void;
  setLoading: (loading: boolean) => void;
  setMustChangePassword: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      storeId: null,
      storeName: null,
      currency: 'USD',
      mustChangePassword: false,

      setAuth: (user, token, refreshToken, storeId, storeName, currency, mustChangePassword = false) => {
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);

        // Connect to WebSocket
        socketManager.connect(token);

        set({
          user,
          token,
          isAuthenticated: true,
          storeId,
          storeName,
          currency,
          mustChangePassword,
        });
      },

      clearAuth: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');

        // Disconnect WebSocket
        socketManager.disconnect();

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          storeId: null,
          storeName: null,
          currency: 'USD',
          mustChangePassword: false,
        });
      },

      setMustChangePassword: (value) => {
        set({ mustChangePassword: value });
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        storeId: state.storeId,
        storeName: state.storeName,
        currency: state.currency,
        mustChangePassword: state.mustChangePassword,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token && state?.isAuthenticated) {
          socketManager.connect(state.token);
        }
      },
    }
  )
);
